import type { QuickFixConfig } from '../quickFixConfig';
import type { GuidedActionConfig } from './issueGuidedActions';
import {
  getValidationRepairOptions,
  isDirectValidationRepairOption,
  isGuidedValidationRepairOption,
  type ValidationRepairOption,
} from './validationRepairOptions';
import type { ValidationIssueFamily } from './validationIssueFamilies';
import type { ValidationIssue } from '../types';
import { normalizeValidationId } from '../validationConstants';
import { formatCategory } from './validationPresentation';
import type {
  ValidationRepairHistoryEntry,
  ValidationRepairItemState,
} from '../../../shared/stores/usePcfValidationStore';

export interface ValidationRepairDetailEvidenceEntry {
  label: string;
  value: string;
}

export interface ValidationRepairDetailChainEntry {
  label: string;
  value: string;
  status: 'ok' | 'warning' | 'error';
}

export interface ValidationRepairDetailLocator {
  scope: string;
  target: string;
  field: string;
  source: string;
  status: string;
}

export type ValidationRepairScope = 'instance' | 'selection' | 'component' | 'group' | 'all';

export interface ValidationRepairScopeOption {
  value: ValidationRepairScope;
  label: string;
  description: string;
  risk: 'low' | 'medium' | 'high';
  warning?: string;
}

export interface ValidationRepairDetailSurgeryConfig {
  targetLabel: string;
  sourcePathLabel: string;
  issueTypeLabel: string;
  manualRepairReason: string;
  expectedOutcome: string;
  scopeOptions: ValidationRepairScopeOption[];
  validationHint: string;
  conditionStudioHint?: string;
}

export interface ValidationRepairDetailItem {
  id: string;
  familyId: string;
  validationId: number;
  severity: ValidationIssueFamily['severity'];
  status: ValidationRepairItemState;
  statusLabel: string;
  itemName: string;
  itemSubtitle: string;
  fieldLabel: string;
  reason: string;
  currentValue: string;
  expectedValue: string;
  currentStateLabel: string;
  predictedPassLabel: string;
  recommendation: string;
  ruleSource: string;
  sourceParameter: string;
  sourceLocation: string;
  evaluationResult: string;
  componentType: string;
  categoryLabel: string;
  impactCount: number;
  elementIds: string[];
  affectedElements: string[];
  rawIssue: ValidationIssue;
  repairOptions: ValidationRepairOption[];
  quickFixes: QuickFixConfig[];
  guidedAction: GuidedActionConfig | null;
  locator: ValidationRepairDetailLocator;
  evidence: ValidationRepairDetailEvidenceEntry[];
  sourcePath: ValidationRepairDetailEvidenceEntry[];
  evaluationChain: ValidationRepairDetailChainEntry[];
  surgeryConfig: ValidationRepairDetailSurgeryConfig;
}

export interface ValidationRepairDetailPresentation {
  areaLabel: string;
  failureLabel: string;
  surgeryScopeLabel: string;
  surgeryDescription: string;
  surgeryEntryLabel: string;
  successLabel: string;
}

const EMPTY_VALIDATION_DETAIL_TOKENS = new Set(['<empty>', '<missing>', '(empty)', '(missing)']);

export function normalizeValidationDetailValue(value: string | undefined): string {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return '';
  }

  return EMPTY_VALIDATION_DETAIL_TOKENS.has(trimmed.toLowerCase()) ? '' : trimmed;
}

function normalizeFieldName(value: string | undefined): string | null {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return null;
  }

  return trimmed.replace(/\s+/g, ' ').toUpperCase();
}

function extractQuotedValues(message: string): string[] {
  const values: string[] = [];
  const singleQuotePattern = /'([^']+)'/g;
  const doubleQuotePattern = /"([^"]+)"/g;

  let match = singleQuotePattern.exec(message);
  while (match) {
    values.push(match[1]);
    match = singleQuotePattern.exec(message);
  }

  match = doubleQuotePattern.exec(message);
  while (match) {
    values.push(match[1]);
    match = doubleQuotePattern.exec(message);
  }

  return values;
}

function extractInstanceLabel(rawValue: string | undefined, elementId: string | undefined): string {
  const trimmed = (rawValue || '').trim();
  if (!trimmed) {
    return elementId ? `Element ${elementId}` : 'Unknown target';
  }

  let label = trimmed;
  label = label.replace(/\s*\[\d+\]\s*/g, ' ');
  label = label.replace(/\s*\(ID:\s*\d+\)\s*/gi, ' ');
  label = label.replace(/\s{2,}/g, ' ').trim();

  return label || (elementId ? `Element ${elementId}` : 'Unknown target');
}

function extractComponentTypeLabel(instanceLabel: string): string {
  if (instanceLabel.includes(':')) {
    return instanceLabel.split(':')[0].trim() || instanceLabel;
  }

  if (instanceLabel.includes(' - ')) {
    return instanceLabel.split(' - ')[0].trim() || instanceLabel;
  }

  return instanceLabel;
}

function classifyEvaluation(message: string): string {
  const upper = message.toUpperCase();

  if (
    upper.includes('INVALID') ||
    upper.includes('UNSUPPORTED') ||
    upper.includes('MISMATCH') ||
    upper.includes('NO MATCHING') ||
    upper.includes('BREAK') ||
    upper.includes('DISCONNECTED')
  ) {
    return 'Invalid';
  }

  if (
    upper.includes('MISSING') ||
    upper.includes('EMPTY') ||
    upper.includes('NO VALUE') ||
    upper.includes('NOT FOUND') ||
    upper.includes('NOT CONNECTED TO A PIPING SYSTEM')
  ) {
    return 'Missing';
  }

  return 'Unresolved';
}

function isEndTypeConnectorPairIssue(issue: ValidationIssue): boolean {
  return normalizeValidationId(issue.ValidationId) === 11;
}

function isObjectTypeEndTypeIssue(issue: ValidationIssue): boolean {
  return normalizeValidationId(issue.ValidationId) === 33;
}

function isGeometryOverlapPairIssue(issue: ValidationIssue): boolean {
  return normalizeValidationId(issue.ValidationId) === 28;
}

function isSystemAssignmentIssue(issue: ValidationIssue): boolean {
  const normalizedValidationId = normalizeValidationId(issue.ValidationId);
  return normalizedValidationId === 8;
}

function isPairScopedIssue(issue: ValidationIssue): boolean {
  return isEndTypeConnectorPairIssue(issue) || isGeometryOverlapPairIssue(issue);
}

function firstNonEmpty(...values: Array<string | undefined>): string {
  return values.map(normalizeValidationDetailValue).find((value) => value.length > 0) || '';
}

function combineUniqueLabels(labels: string[], separator = ' ↔ '): string {
  const uniqueLabels = Array.from(new Set(labels.map((label) => label.trim()).filter(Boolean)));
  return uniqueLabels.join(separator);
}

function resolveEndTypeLookupLabel(
  currentValue: string | undefined,
  connectedValue: string | undefined,
  fallback: string
): string {
  const currentLabel = normalizeFieldName(currentValue);
  const connectedLabel = normalizeFieldName(connectedValue);
  const combined = combineUniqueLabels([currentLabel || '', connectedLabel || '']);
  return combined || fallback;
}

function resolveEndTypeRoleLabel(issue: ValidationIssue): string {
  const currentRole = (issue.AdditionalData?.currentConnectorRole || '').trim();
  const connectedRole = (issue.AdditionalData?.connectedConnectorRole || '').trim();
  const combinedRole = combineUniqueLabels([currentRole, connectedRole]);
  if (combinedRole) {
    return combinedRole;
  }

  return resolveEndTypeLookupLabel(
    currentRole,
    connectedRole,
    resolveEndTypeLookupLabel(
      issue.AdditionalData?.currentEndTypeParameterName,
      issue.AdditionalData?.connectedEndTypeParameterName,
      'Connected END-TYPE assignment'
    )
  );
}

function resolveEndTypeCurrentValue(issue: ValidationIssue): string {
  const currentValue = firstNonEmpty(
    issue.AdditionalData?.currentEndTypeRaw,
    issue.AdditionalData?.currentEndTypeCanonical
  ) || '<missing>';
  const connectedValue = firstNonEmpty(
    issue.AdditionalData?.connectedEndTypeRaw,
    issue.AdditionalData?.connectedEndTypeCanonical
  ) || '<missing>';

  return currentValue === connectedValue
    ? currentValue
    : `${currentValue} versus ${connectedValue}`;
}

function resolveEndTypeExpectedValue(issue: ValidationIssue): string {
  const currentRole = (issue.AdditionalData?.currentConnectorRole || '').trim();
  const connectedRole = (issue.AdditionalData?.connectedConnectorRole || '').trim();
  const currentParam = normalizeFieldName(issue.AdditionalData?.currentEndTypeParameterName);
  const connectedParam = normalizeFieldName(issue.AdditionalData?.connectedEndTypeParameterName);

  if (currentRole && connectedRole) {
    if (currentRole === connectedRole) {
      return `matching non-empty ${currentRole} value on both connected connectors`;
    }

    return `matching compatible END-TYPE values for ${currentRole} and ${connectedRole}`;
  }

  if (currentParam && connectedParam) {
    if (currentParam === connectedParam) {
      return `matching non-empty ${currentParam} value on both connected connectors`;
    }

    return `matching compatible ${currentParam} and ${connectedParam} values across the connected interface`;
  }

  return 'matching non-empty END-TYPE assignments on both connected connectors';
}

function formatEndTypeSignatureForDisplay(signature: string | undefined): string {
  const trimmed = (signature || '').trim();
  if (!trimmed) {
    return '';
  }

  return trimmed
    .split('|')
    .map((option) => {
      const normalizedSlots = option
        .split('/')
        .map(normalizeValidationDetailValue)
        .filter(Boolean);

      return normalizedSlots.length > 0 ? normalizedSlots.join(' / ') : option.trim();
    })
    .join(' | ');
}

function resolveObjectTypeEndTypeCurrentValue(issue: ValidationIssue): string {
  return (
    formatEndTypeSignatureForDisplay(
      [
        issue.AdditionalData?.actualEndType1,
        issue.AdditionalData?.actualEndType2,
        issue.AdditionalData?.actualEndType3,
      ]
        .map((value) => value || '')
        .join('/')
    ) ||
    firstNonEmpty(issue.AdditionalData?.currentValue) ||
    '<missing>'
  );
}

function resolveObjectTypeEndTypeExpectedValue(issue: ValidationIssue): string {
  return (
    formatEndTypeSignatureForDisplay(issue.AdditionalData?.expectedEndTypes) ||
    'END-TYPE values compatible with the OBJECT-TYPE/SKEY etalon'
  );
}

function resolveSystemAssignmentLookupLabel(issue: ValidationIssue): string {
  return firstNonEmpty(
    issue.AdditionalData?.parameterName,
    'MEP system / end-connector system'
  );
}

function resolveSystemAssignmentCurrentValue(issue: ValidationIssue): string {
  return firstNonEmpty(
    issue.AdditionalData?.currentValue,
    issue.AdditionalData?.currentSystemName,
    'no active piping system connection'
  );
}

function resolveSystemAssignmentExpectedValue(issue: ValidationIssue): string {
  return firstNonEmpty(
    issue.AdditionalData?.expectedValue,
    'connected to a valid piping system'
  );
}

function resolveGeometryOverlapLookupLabel(issue: ValidationIssue): string {
  return firstNonEmpty(
    issue.AdditionalData?.parameterName,
    'Bounding box / solid intersection'
  );
}

function resolveGeometryOverlapCurrentValue(issue: ValidationIssue): string {
  return firstNonEmpty(
    issue.AdditionalData?.currentValue,
    'overlap detected between unconnected elements'
  );
}

function resolveGeometryOverlapExpectedValue(issue: ValidationIssue): string {
  return firstNonEmpty(
    issue.AdditionalData?.expectedValue,
    'no unintended geometric overlap between the affected elements'
  );
}

function resolveEvaluationResult(issue: ValidationIssue): string {
  if (isGeometryOverlapPairIssue(issue)) {
    return 'Overlap detected';
  }

  return classifyEvaluation(issue.Message);
}

function resolveRepairDetailSourceLookup(issue: ValidationIssue, fieldLabel: string): string {
  if (isEndTypeConnectorPairIssue(issue)) {
    return resolveEndTypeLookupLabel(
      issue.AdditionalData?.currentEndTypeParameterName,
      issue.AdditionalData?.connectedEndTypeParameterName,
      fieldLabel
    );
  }

  if (isObjectTypeEndTypeIssue(issue)) {
    return combineUniqueLabels([
      issue.AdditionalData?.endType1ParameterName || 'END-TYPE_1',
      issue.AdditionalData?.endType2ParameterName || 'END-TYPE_2',
      issue.AdditionalData?.endType3ParameterName || 'END-TYPE_3',
    ]);
  }

  if (isSystemAssignmentIssue(issue)) {
    return resolveSystemAssignmentLookupLabel(issue);
  }

  if (isGeometryOverlapPairIssue(issue)) {
    return resolveGeometryOverlapLookupLabel(issue);
  }

  return issue.AdditionalData?.parameterName || fieldLabel;
}

function buildPairItemName(issue: ValidationIssue): string {
  const endpoints = issue.ElementIds.map((elementId, index) => {
    const rawLabel = issue.AffectedElements[index] || issue.AffectedElements[0];
    const baseLabel = extractInstanceLabel(rawLabel, elementId);
    return elementId ? `${baseLabel} (${elementId})` : baseLabel;
  });

  return combineUniqueLabels(endpoints) || 'Connected interface';
}

function buildPairComponentType(issue: ValidationIssue): string {
  const componentTypes = issue.AffectedElements.map((rawLabel, index) =>
    extractComponentTypeLabel(extractInstanceLabel(rawLabel, issue.ElementIds[index]))
  );

  return combineUniqueLabels(componentTypes) || 'Connected interface';
}

function resolveFieldLabel(issue: ValidationIssue, family: ValidationIssueFamily): string {
  if (isEndTypeConnectorPairIssue(issue)) {
    return resolveEndTypeRoleLabel(issue);
  }

  if (isObjectTypeEndTypeIssue(issue)) {
    return 'OBJECT-TYPE/SKEY END-TYPE etalon';
  }

  if (isSystemAssignmentIssue(issue)) {
    return 'Piping system assignment';
  }

  if (isGeometryOverlapPairIssue(issue)) {
    return 'Spatial overlap';
  }

  const fromData = normalizeFieldName(issue.AdditionalData?.parameterName);
  if (fromData) {
    return fromData;
  }

  const quotedValues = extractQuotedValues(issue.Message);
  if (quotedValues.length > 0) {
    return quotedValues[0];
  }

  if (family.category === 'connectors') {
    return 'Connector state';
  }

  if (family.category === 'mapping') {
    return 'Mapping entry';
  }

  return family.targetLabel;
}

function resolveCurrentValue(issue: ValidationIssue, fieldLabel: string): string {
  if (isEndTypeConnectorPairIssue(issue)) {
    return resolveEndTypeCurrentValue(issue);
  }

  if (isObjectTypeEndTypeIssue(issue)) {
    return resolveObjectTypeEndTypeCurrentValue(issue);
  }

  if (isSystemAssignmentIssue(issue)) {
    return resolveSystemAssignmentCurrentValue(issue);
  }

  if (isGeometryOverlapPairIssue(issue)) {
    return resolveGeometryOverlapCurrentValue(issue);
  }

  const fromData = issue.AdditionalData?.currentValue?.trim();
  if (fromData) {
    return fromData;
  }

  const quotedValues = extractQuotedValues(issue.Message);
  if (quotedValues.length > 1) {
    return quotedValues[1];
  }

  const evaluation = classifyEvaluation(issue.Message);
  if (evaluation === 'Missing') {
    return 'empty';
  }

  return quotedValues[0] || 'unresolved';
}

function resolveExpectedValue(issue: ValidationIssue, family: ValidationIssueFamily, fieldLabel: string): string {
  if (isEndTypeConnectorPairIssue(issue)) {
    return resolveEndTypeExpectedValue(issue);
  }

  if (isObjectTypeEndTypeIssue(issue)) {
    return resolveObjectTypeEndTypeExpectedValue(issue);
  }

  if (isSystemAssignmentIssue(issue)) {
    return resolveSystemAssignmentExpectedValue(issue);
  }

  if (isGeometryOverlapPairIssue(issue)) {
    return resolveGeometryOverlapExpectedValue(issue);
  }

  if (family.category === 'connectors') {
    return 'connected, aligned connector state';
  }

  if (family.category === 'mapping') {
    return `resolved mapping for ${fieldLabel}`;
  }

  if (family.isRequiredOnly) {
    return `non-empty ${fieldLabel} value`;
  }

  return issue.Recommendation || `valid ${fieldLabel} value`;
}

function resolveSourceLocation(issue: ValidationIssue, family: ValidationIssueFamily): string {
  if (isEndTypeConnectorPairIssue(issue)) {
    return 'Validation runtime / connected connector pair';
  }

  if (isSystemAssignmentIssue(issue)) {
    return 'Validation runtime / piping system graph';
  }

  if (isGeometryOverlapPairIssue(issue)) {
    return 'Validation runtime / geometric intersection';
  }

  switch (family.targetPage) {
    case 'mapping':
      return 'Export mapping configuration';
    case 'quick-fill':
      return 'Quick Fill and mapped Revit parameter';
    case 'connectors':
      return 'Connector binding / model geometry';
    case 'properties':
      return 'Workflow property mapping';
    default:
      return 'Validation runtime';
  }
}

function resolveRuleSourceLabel(family: ValidationIssueFamily, issue: ValidationIssue): string {
  if (isEndTypeConnectorPairIssue(issue)) {
    return 'END-TYPE connector pair / Validation';
  }

  if (isSystemAssignmentIssue(issue)) {
    return 'Piping system connectivity / Validation';
  }

  if (isGeometryOverlapPairIssue(issue)) {
    return 'Spatial overlap / Validation';
  }

  return `${formatCategory(family.category, 'short')} / ${family.targetLabel}`;
}

function resolveLocatorScope(family: ValidationIssueFamily, issue: ValidationIssue): string {
  if (isEndTypeConnectorPairIssue(issue)) {
    return 'Connector pair';
  }

  if (isGeometryOverlapPairIssue(issue)) {
    return 'Element pair';
  }

  return family.category === 'connectors' ? 'Connector' : 'Component';
}

function resolvePresentation(family: ValidationIssueFamily): ValidationRepairDetailPresentation {
  switch (family.category) {
    case 'mapping':
      return {
        areaLabel: 'Mapping',
        failureLabel: 'Mapped field failure',
        surgeryScopeLabel: 'Mapping override scope',
        surgeryDescription: 'Override the mapped value or open Mapping for a precise correction.',
        surgeryEntryLabel: 'Start mapping detail repair',
        successLabel: 'Mapping override staged',
      };
    case 'required-export':
      return {
        areaLabel: 'Required export data',
        failureLabel: 'Export field failure',
        surgeryScopeLabel: 'Export value scope',
        surgeryDescription: 'Patch the exact export value with a scoped override, preview the change, then revalidate the affected rule.',
        surgeryEntryLabel: 'Start export detail repair',
        successLabel: 'Export value override staged',
      };
    case 'connectors':
      return {
        areaLabel: 'Connectors',
        failureLabel: 'Connector failure',
        surgeryScopeLabel: 'Connector repair scope',
        surgeryDescription: 'Connector repair is model-side. Use highlighting and scoped reruns to verify the exact repair safely.',
        surgeryEntryLabel: 'Start connector detail repair',
        successLabel: 'Connector repair verified',
      };
    case 'properties':
      return {
        areaLabel: 'Properties',
        failureLabel: 'Workflow property failure',
        surgeryScopeLabel: 'Property override scope',
        surgeryDescription: 'Apply a targeted property correction and preview its scope before committing the manual override.',
        surgeryEntryLabel: 'Start property detail repair',
        successLabel: 'Property override staged',
      };
    default:
      return {
        areaLabel: 'Objects / Components',
        failureLabel: 'Classification failure',
        surgeryScopeLabel: 'Classification override scope',
        surgeryDescription: 'Use a deliberate object/classification override for the selected target and verify the rule outcome before applying broadly.',
        surgeryEntryLabel: 'Start classification detail repair',
        successLabel: 'Classification override staged',
      };
  }
}

function toStatusLabel(status: ValidationRepairItemState): string {
  switch (status) {
    case 'resolved':
      return 'Resolved';
    case 'manual-edited':
      return 'Edited';
    case 'revalidated':
      return 'Validated';
    default:
      return 'Unresolved';
  }
}

function resolveCurrentStateLabel(family: ValidationIssueFamily): string {
  if (family.category === 'connectors') {
    return 'connection unresolved';
  }

  return family.isBlocking ? 'export readiness blocked' : 'repair target unresolved';
}

function resolvePredictedPassLabel(family: ValidationIssueFamily): string {
  if (family.category === 'connectors') {
    return 'connector validation likely clears';
  }

  return family.isBlocking
    ? 'passes required export validation'
    : 'passes the current validation rule';
}

function buildScopeOptionTemplates(
  family: ValidationIssueFamily,
  componentType: string
): ValidationRepairScopeOption[] {
  switch (family.category) {
    case 'mapping':
      return [
        {
          value: 'instance',
          label: 'This instance only',
          description: 'Patch only the selected mapped instance.',
          risk: 'low',
        },
        {
          value: 'selection',
          label: 'Selected failing instances',
          description: 'Apply the override to the currently failing instances tied to this mapping check.',
          risk: 'medium',
        },
        {
          value: 'group',
          label: 'This issue group',
          description: 'Apply the override across the grouped mapping issue only when the failure is uniform.',
          risk: 'high',
          warning: 'Broad mapping edits can change export behavior for multiple mapped fields at once.',
        },
        {
          value: 'all',
          label: 'All matching unresolved items',
          description: 'Apply to every unresolved mapping item that matches this issue signature.',
          risk: 'high',
          warning: 'Use only when the same mapped-field correction is valid everywhere.',
        },
      ];
    case 'required-export':
      return [
        {
          value: 'instance',
          label: 'This instance only',
          description: 'Patch the missing export value on the selected target only.',
          risk: 'low',
        },
        {
          value: 'selection',
          label: 'Selected failing instances',
          description: 'Apply the value to the instances currently failing under this validation rule.',
          risk: 'medium',
        },
        {
          value: 'component',
          label: 'This component family',
          description: `Apply the same export correction to ${componentType || 'matching'} items only if they share the same root cause.`,
          risk: 'medium',
          warning: 'Component-wide edits can affect related export outputs beyond the selected item.',
        },
        {
          value: 'group',
          label: 'This issue group',
          description: 'Apply the correction across the whole unresolved issue group when the data gap is consistent.',
          risk: 'high',
          warning: 'Use only when every item in the group should receive the same value.',
        },
        {
          value: 'all',
          label: 'All matching unresolved items',
          description: 'Apply the same correction to every unresolved item that matches this validation signature.',
          risk: 'high',
          warning: 'This is the broadest scope and should be used only after verifying the preview carefully.',
        },
      ];
    case 'connectors':
      return [
        {
          value: 'instance',
          label: 'This connector pair',
          description: 'Inspect and validate only the selected connector relationship.',
          risk: 'low',
        },
        {
          value: 'selection',
          label: 'Selected failing instances',
          description: 'Revalidate the currently failing connector set after completing model-side edits.',
          risk: 'medium',
        },
        {
          value: 'group',
          label: 'This issue group',
          description: 'Use only when the same connector condition repeats across the group.',
          risk: 'high',
          warning: 'Connector group validation can hide item-level differences if the physical fixes vary.',
        },
      ];
    case 'properties':
      return [
        {
          value: 'instance',
          label: 'This instance only',
          description: 'Apply the property correction only to the selected target.',
          risk: 'low',
        },
        {
          value: 'selection',
          label: 'Selected failing instances',
          description: 'Apply the correction to the currently failing instances tied to this rule.',
          risk: 'medium',
        },
        {
          value: 'component',
          label: 'This component family',
          description: `Apply the same property correction across ${componentType || 'matching'} items when the issue is systemic.`,
          risk: 'medium',
          warning: 'Component-family edits can change downstream workflow logic for multiple items.',
        },
        {
          value: 'group',
          label: 'This issue group',
          description: 'Apply to all unresolved items in this property issue group only when the correction is consistent.',
          risk: 'high',
          warning: 'Use this only when the same workflow property should be set across the group.',
        },
      ];
    default:
      return [
        {
          value: 'instance',
          label: 'This instance only',
          description: 'Correct the selected classification target only.',
          risk: 'low',
        },
        {
          value: 'selection',
          label: 'Selected failing instances',
          description: 'Apply the classification correction to the currently failing instances under this validation check.',
          risk: 'medium',
        },
        {
          value: 'component',
          label: 'This component family',
          description: `Apply across ${componentType || 'matching'} items that share the same classification failure.`,
          risk: 'medium',
          warning: 'Component-family edits can shift downstream type mapping and export categorization.',
        },
        {
          value: 'group',
          label: 'This issue group',
          description: 'Apply across the grouped classification issue when the fix is intentionally uniform.',
          risk: 'high',
          warning: 'Confirm that every unresolved item should resolve to the same classification outcome.',
        },
        {
          value: 'all',
          label: 'All matching unresolved items',
          description: 'Apply to all unresolved items that share the same classification signature.',
          risk: 'high',
          warning: 'Broad classification edits should be used only after validating a narrower sample first.',
        },
      ];
  }
}

function buildSurgeryConfig(
  family: ValidationIssueFamily,
  fieldLabel: string,
  componentType: string
): Omit<ValidationRepairDetailSurgeryConfig, 'scopeOptions'> {
  switch (family.category) {
    case 'mapping':
      return {
        targetLabel: 'Mapped field override',
        sourcePathLabel: 'Mapping table -> target field -> runtime export field',
        issueTypeLabel: 'Mapping failure',
        manualRepairReason: 'This mapped failure needs exact review of the failing field before changing export configuration broadly.',
        expectedOutcome: 'Mapped export field resolves with the corrected value or source.',
        validationHint: 'Validate the selected mapping rule after applying the override to confirm the export field resolves.',
        conditionStudioHint: 'Use Condition Studio only for true scope exceptions. Auto-fill adjustments and other Quick Fill repairs are direct fixes, not exclusions.',
      };
    case 'required-export':
      return {
        targetLabel: 'Export field value',
        sourcePathLabel: 'Quick Fill and mapped parameter → export field',
        issueTypeLabel: 'Required export value failure',
        manualRepairReason: 'This export blocker does not map cleanly to a safe automatic fix and needs exact review of the failing point.',
        expectedOutcome: 'Required export validation clears for the selected target and scope.',
        validationHint: 'Validate the current issue immediately after the staged repair to confirm the export blocker clears.',
        conditionStudioHint: 'Use Condition Studio only for true exclusions or scope changes. Auto-fill adjustments and other Quick Fill repairs are direct fixes, not exclusions.',
      };
    case 'connectors':
      return {
        targetLabel: 'Connector fault target',
        sourcePathLabel: 'Connector endpoints -> binding -> geometry relationship',
        issueTypeLabel: 'Connector failure',
        manualRepairReason: 'Connector blockers need exact inspection and controlled model-side repair rather than a rerun-first workflow.',
        expectedOutcome: 'Connector validation rerun reflects the repaired physical relationship.',
        validationHint: 'Connector repair is model-side. Highlight the failing point, repair it in-model, then validate this issue again.',
      };
    case 'properties':
      return {
        targetLabel: 'Workflow property override',
        sourcePathLabel: 'Workflow property mapping -> validation rule',
        issueTypeLabel: 'Workflow property failure',
        manualRepairReason: 'This workflow issue needs exact repair context because it does not map cleanly to a direct automatic fix surface.',
        expectedOutcome: 'Workflow property validation clears without widening rule scope.',
        validationHint: 'Validate the current property rule to confirm downstream workflow fields resolve correctly.',
        conditionStudioHint: 'Use Condition Studio only if the rule itself is over-broad or these cases are legitimate workflow exceptions.',
      };
    default:
      return {
        targetLabel: 'Classification override',
        sourcePathLabel: 'Component classification -> export object typing',
        issueTypeLabel: `${componentType || fieldLabel} classification failure`,
        manualRepairReason: 'This classification issue needs exact target review before broad edits are applied.',
        expectedOutcome: 'Classification repair satisfies the current export typing rule.',
        validationHint: 'Validate the current classification issue after the staged repair to confirm the target now satisfies the expected type rule.',
      };
  }
}

export function gatherRepairScopeElementIds(
  scope: ValidationRepairScope,
  family: ValidationIssueFamily,
  item: Pick<ValidationRepairDetailItem, 'id' | 'validationId' | 'elementIds' | 'componentType' | 'status'>,
  items: Array<Pick<ValidationRepairDetailItem, 'id' | 'validationId' | 'elementIds' | 'componentType' | 'status'>>
): string[] {
  if (scope === 'instance') {
    return item.elementIds;
  }

  if (scope === 'selection') {
    return Array.from(
      new Set(
        items
          .filter((candidate) => candidate.validationId === item.validationId)
          .flatMap((candidate) => candidate.elementIds)
      )
    );
  }

  if (scope === 'component') {
    return Array.from(
      new Set(
        items
          .filter((candidate) => candidate.componentType === item.componentType)
          .flatMap((candidate) => candidate.elementIds)
      )
    );
  }

  if (scope === 'group') {
    return family.impactedElementIds.length > 0
      ? family.impactedElementIds
      : Array.from(new Set(items.flatMap((candidate) => candidate.elementIds)));
  }

  return Array.from(
    new Set(
      items
        .filter((candidate) => candidate.status !== 'resolved')
        .flatMap((candidate) => candidate.elementIds)
    )
  );
}

interface RepairScopeIndex {
  selectionElementIdsByValidationId: Map<number, Set<string>>;
  componentElementIdsByType: Map<string, Set<string>>;
  groupElementCount: number;
  unresolvedElementIds: Set<string>;
}

function addElementIds(target: Set<string>, elementIds: string[]) {
  elementIds.forEach((elementId) => target.add(elementId));
}

function getOrCreateElementSet<TKey>(map: Map<TKey, Set<string>>, key: TKey): Set<string> {
  const existing = map.get(key);
  if (existing) {
    return existing;
  }

  const created = new Set<string>();
  map.set(key, created);
  return created;
}

function buildRepairScopeIndex(
  family: ValidationIssueFamily,
  items: Array<Pick<ValidationRepairDetailItem, 'validationId' | 'elementIds' | 'componentType' | 'status'>>
): RepairScopeIndex {
  const allElementIds = new Set<string>();
  const unresolvedElementIds = new Set<string>();
  const selectionElementIdsByValidationId = new Map<number, Set<string>>();
  const componentElementIdsByType = new Map<string, Set<string>>();

  items.forEach((item) => {
    addElementIds(allElementIds, item.elementIds);
    addElementIds(
      getOrCreateElementSet(selectionElementIdsByValidationId, item.validationId),
      item.elementIds
    );
    addElementIds(
      getOrCreateElementSet(componentElementIdsByType, item.componentType),
      item.elementIds
    );

    if (item.status !== 'resolved') {
      addElementIds(unresolvedElementIds, item.elementIds);
    }
  });

  return {
    selectionElementIdsByValidationId,
    componentElementIdsByType,
    groupElementCount: family.impactedElementIds.length > 0
      ? family.impactedElementIds.length
      : allElementIds.size,
    unresolvedElementIds,
  };
}

function getIndexedRepairScopeElementCount(
  scope: ValidationRepairScope,
  item: Pick<ValidationRepairDetailItem, 'validationId' | 'elementIds' | 'componentType'>,
  scopeIndex: RepairScopeIndex
): number {
  switch (scope) {
    case 'instance':
      return item.elementIds.length;
    case 'selection':
      return scopeIndex.selectionElementIdsByValidationId.get(item.validationId)?.size ?? 0;
    case 'component':
      return scopeIndex.componentElementIdsByType.get(item.componentType)?.size ?? 0;
    case 'group':
      return scopeIndex.groupElementCount;
    case 'all':
      return scopeIndex.unresolvedElementIds.size;
    default:
      return 0;
  }
}

function buildResolvedScopeOptions(
  family: ValidationIssueFamily,
  item: Pick<ValidationRepairDetailItem, 'validationId' | 'elementIds' | 'componentType'>,
  componentType: string,
  scopeIndex: RepairScopeIndex
): ValidationRepairScopeOption[] {
  return buildScopeOptionTemplates(family, componentType).filter((option) => {
    const scopeElementCount = getIndexedRepairScopeElementCount(option.value, item, scopeIndex);
    if (scopeElementCount === 0) {
      return false;
    }

    if (option.value !== 'instance' && scopeElementCount <= item.elementIds.length) {
      return false;
    }

    return true;
  });
}

export function buildValidationRepairDetailItems(
  family: ValidationIssueFamily,
  repairItemStates: Record<string, ValidationRepairItemState> = {}
): ValidationRepairDetailItem[] {
  const categoryLabel = formatCategory(family.category, 'short');
  const baseItems = family.rawIssues.flatMap((issue, issueIndex) => {
    const sourceLocation = resolveSourceLocation(issue, family);
    const pairCount = isPairScopedIssue(issue)
      ? 1
      : Math.max(issue.AffectedElements.length, issue.ElementIds.length, 1);
    const fieldLabel = resolveFieldLabel(issue, family);
    const sourceLookup = resolveRepairDetailSourceLookup(issue, fieldLabel);
    const currentValue = resolveCurrentValue(issue, fieldLabel);
    const expectedValue = resolveExpectedValue(issue, family, fieldLabel);
    const evaluationResult = resolveEvaluationResult(issue);
    const repairOptions = getValidationRepairOptions(issue);
    const quickFixes = repairOptions
      .filter(isDirectValidationRepairOption)
      .map((option) => option.quickFix);
    const guidedAction =
      repairOptions.find(isGuidedValidationRepairOption)?.guidedAction ?? null;

    return Array.from({ length: pairCount }, (_, pairIndex) => {
      const pairScoped = isPairScopedIssue(issue);
      const elementId = pairScoped
        ? issue.ElementIds.join(',')
        : issue.ElementIds[pairIndex] || issue.ElementIds[0];
      const rawLabel = pairScoped
        ? issue.AffectedElements.join(' ↔ ')
        : issue.AffectedElements[pairIndex] || issue.AffectedElements[0];
      const itemName = pairScoped
        ? buildPairItemName(issue)
        : extractInstanceLabel(rawLabel, elementId);
      const componentType = pairScoped
        ? buildPairComponentType(issue)
        : extractComponentTypeLabel(itemName);
      const id = `${family.id}::${issue.ValidationId}::${issueIndex}::${pairScoped ? 'pair' : pairIndex.toString()}::${elementId || 'no-element'}::${fieldLabel}`;
      const status = repairItemStates[id] || 'unresolved';
      const ruleSource = resolveRuleSourceLabel(family, issue);
      const locator: ValidationRepairDetailLocator = {
        scope: resolveLocatorScope(family, issue),
        target: itemName,
        field: fieldLabel,
        source: sourceLocation,
        status: evaluationResult,
      };
      const surgeryConfigBase = buildSurgeryConfig(family, fieldLabel, componentType);

      return {
        id,
        familyId: family.id,
        validationId: issue.ValidationId,
        severity: family.severity,
        status,
        statusLabel: toStatusLabel(status),
        itemName,
        itemSubtitle: `${componentType} · ${fieldLabel}`,
        fieldLabel,
        reason: issue.Message,
        currentValue,
        expectedValue,
        currentStateLabel: resolveCurrentStateLabel(family),
        predictedPassLabel: resolvePredictedPassLabel(family),
        recommendation: issue.Recommendation,
        ruleSource,
        sourceParameter: sourceLookup,
        sourceLocation,
        evaluationResult,
        componentType,
        categoryLabel,
        impactCount: issue.ElementIds.length || family.affectedCount,
        elementIds: pairScoped ? issue.ElementIds : elementId ? [elementId] : issue.ElementIds,
        affectedElements: pairScoped ? issue.AffectedElements : rawLabel ? [rawLabel] : issue.AffectedElements,
        rawIssue: issue,
        repairOptions,
        quickFixes,
        guidedAction,
        locator,
        evidence: [
          { label: 'Source parameter', value: sourceLookup },
          { label: 'Source location', value: sourceLocation },
          { label: 'Rule source', value: ruleSource },
          { label: 'Evaluation result', value: evaluationResult },
          { label: 'Component type', value: componentType },
        ],
        sourcePath: [
          { label: 'Target field', value: fieldLabel },
          { label: 'Source lookup', value: sourceLookup },
          { label: 'Origin', value: sourceLocation },
          { label: 'Fallback chain', value: family.targetLabel },
        ],
        evaluationChain: [
          { label: 'Target field', value: fieldLabel, status: 'ok' },
          { label: 'Source lookup', value: sourceLookup, status: 'ok' },
          { label: 'Rule evaluation', value: evaluationResult, status: evaluationResult === 'Missing' ? 'warning' : 'error' },
          { label: 'Failure point', value: issue.Message, status: 'error' },
          { label: 'Export result', value: family.isBlocking ? 'Blocked export output' : 'Unresolved repair target', status: family.isBlocking ? 'error' : 'warning' },
        ],
        surgeryConfig: {
          ...surgeryConfigBase,
          scopeOptions: [],
        },
      } satisfies ValidationRepairDetailItem;
    });
  });

  const scopeIndex = buildRepairScopeIndex(family, baseItems);

  return baseItems.map((item) => ({
    ...item,
    surgeryConfig: {
      ...item.surgeryConfig,
      scopeOptions: buildResolvedScopeOptions(family, item, item.componentType, scopeIndex),
    },
  }));
}

export function resolveValidationRepairInitialItemId(
  items: ValidationRepairDetailItem[],
  selectedItemId: string | null
): string | null {
  if (selectedItemId && items.some((item) => item.id === selectedItemId)) {
    return selectedItemId;
  }

  return items[0]?.id ?? null;
}

export function getValidationRepairPresentation(family: ValidationIssueFamily) {
  return resolvePresentation(family);
}

export function getValidationRepairHistoryForFamily(
  familyId: string,
  repairHistory: ValidationRepairHistoryEntry[]
) {
  return repairHistory.filter((entry) => entry.familyId === familyId);
}
